import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PageHead } from "../GtaxShell";
import { ALL_TIERS, usd } from "../data";
import { CLAUSES } from "../legal";
import { COUNTRIES } from "../countries";
import { KycPanel } from "./KycPanel";
import { AgreementCopy } from "../AgreementCopy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/gtax`;
const FIELDS = [["full_name", "Full legal name", "text"], ["email", "Email", "email"], ["phone", "Phone / WhatsApp", "tel"], ["country", "Country of residence", "text"]];

const WALLET_NETS = [["usdt_bep20", "USDT · BNB Smart Chain (BEP-20)"], ["usdc_erc20", "USDC · Ethereum (ERC-20)"], ["usdt_trc20", "USDT · Tron (TRC-20)"]];
const WALLET_OK = { usdt_bep20: /^0x[0-9a-fA-F]{40}$/, usdc_erc20: /^0x[0-9a-fA-F]{40}$/, usdt_trc20: /^T[1-9A-HJ-NP-Za-km-z]{33}$/ };

const tgInitData = () => {
  const fromHash = new URLSearchParams(window.location.hash.slice(1)).get("tgWebAppData");
  if (fromHash) sessionStorage.setItem("gtax_tg", fromHash);
  return window.Telegram?.WebApp?.initData || fromHash || sessionStorage.getItem("gtax_tg") || undefined;
};

const Steps = ({ step, tg }) => (
  <div className="flex gap-2 md:gap-3 mb-12 text-[10px] md:text-xs tracking-wider md:tracking-widest uppercase" data-testid="gtax-access-steps">
    {["Your details", "Digital agreement", tg ? "Telegram" : "Identity check", "Settlement"].map((s, i) => (
      <div key={s} className={`flex-1 pt-4 border-t-2 ${i <= step ? "border-[color:var(--gold)] text-[color:var(--ink)]" : "border-[color:var(--line)] text-[color:var(--mute)]"}`}>
        {String(i + 1).padStart(2, "0")} · {s}
      </div>
    ))}
  </div>
);

const Label = ({ children }) => <label className="block text-xs tracking-widest uppercase text-[color:var(--mute)]">{children}</label>;

const Details = ({ f, set, next }) => {
  const tier = ALL_TIERS.find((t) => t.id === f.tier);
  const ok = FIELDS.every(([k]) => f[k].trim()) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()) && f.wallet_network && WALLET_OK[f.wallet_network].test(f.wallet.trim()) && Number(f.amount) >= tier.min;
  return (
    <div className="space-y-8" data-testid="gtax-access-details">
      <div className="grid md:grid-cols-2 gap-8">
        {FIELDS.map(([k, l, t]) => (
          <div key={k}><Label>{l}</Label>
            {k === "country" ? (
              <select value={f.country} onChange={set("country")} className="w-full p-3 mt-2 text-lg" data-testid="gtax-access-country">
                <option value="">Select your country</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : <input type={t} value={f[k]} onChange={set(k)} className="w-full py-3 text-lg" data-testid={`gtax-access-${k}`} />}
          </div>
        ))}
        <div>
          <Label>Membership tier</Label>
          <select value={f.tier} onChange={set("tier")} className="w-full p-3 mt-2 text-lg" data-testid="gtax-access-tier">
            {ALL_TIERS.map((t) => <option key={t.id} value={t.id}>{`${t.name} · from ${usd(t.min)}`}</option>)}
          </select>
        </div>
        <div><Label>Intended allocation (USD)</Label><input type="number" value={f.amount} onChange={set("amount")} className="w-full py-3 text-lg" data-testid="gtax-access-amount" /></div>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        <div>
          <Label>Redemption wallet network</Label>
          <select value={f.wallet_network} onChange={set("wallet_network")} className="w-full p-3 mt-2 text-lg" data-testid="gtax-access-wallet-network">
            <option value="">Select network</option>
            {WALLET_NETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div className="md:col-span-2"><Label>Your redemption wallet address</Label><p className="text-xs text-[color:var(--mute)] mt-1">Your own wallet where withdrawals will be paid back to you.</p><input value={f.wallet} onChange={set("wallet")} className="w-full py-3 text-lg font-mono" data-testid="gtax-access-wallet" /></div>
      </div>
      <p className="text-sm p-4 border border-[color:var(--gold)]" data-testid="gtax-access-same-chain">Please make sure you send the money on the same blockchain network you choose here. Funds sent on a different network may be lost.</p>
      {f.wallet.trim() && f.wallet_network && !WALLET_OK[f.wallet_network].test(f.wallet.trim()) && <p className="text-sm text-[#A0523F]" data-testid="gtax-access-wallet-error">This does not look like a valid address for the selected network.</p>}
      {Number(f.amount) < tier.min && <p className="text-sm text-[#A0523F]" data-testid="gtax-access-min-error">{tier.name} requires a minimum allocation of {usd(tier.min)}.</p>}
      <button disabled={!ok} onClick={next} className="gx-btn disabled:opacity-40" data-testid="gtax-access-next">Continue to agreement</button>
    </div>
  );
};

const AgreementDoc = ({ doc, onRead }) => {
  const check = (e) => { const el = e.currentTarget; if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) onRead(); };
  return (
    <div onScroll={check} className="max-h-[60vh] overflow-y-auto border border-[color:var(--line)] p-6 md:p-10 space-y-6 gx-legal" data-testid="gtax-agreement-doc">
      <div><div className="gx-eyebrow">{doc.version}</div><h3 className="gx-serif text-3xl mt-2">{doc.title}</h3></div>
      {doc.sections.map(([h, ...ps]) => (
        <div key={h}><h4 className="gx-caps text-lg">{h}</h4>{ps.map((p) => <p key={p.slice(0, 30)} className="mt-3 text-[color:var(--mute)] leading-relaxed">{p}</p>)}</div>
      ))}
      <div><h4 className="gx-caps text-lg">Schedule · Tiers</h4>
        <table className="w-full mt-3 text-sm"><tbody>{doc.schedule.map((r) => <tr key={r[0]} className="border-b border-[color:var(--line)]">{r.map((c) => <td key={c}>{c}</td>)}</tr>)}</tbody></table>
      </div>
      <p className="text-sm gx-gold" data-testid="gtax-agreement-end">End of agreement.</p>
    </div>
  );
};

const Agreement = ({ f, accepted, toggle, sig, setSig, busy, submit, back, read, setRead }) => {
  const [doc, setDoc] = useState(null);
  useEffect(() => { axios.get(`${API}/agreement`).then(({ data }) => setDoc(data)).catch(() => toast.error("Could not load the agreement. Please refresh.")); }, []);
  const all = CLAUSES.every((c) => accepted.includes(c.id));
  const sigOk = sig.trim().toLowerCase() === f.full_name.trim().toLowerCase();
  return (
    <div className="space-y-8" data-testid="gtax-access-agreement">
      <p className="text-[color:var(--mute)]">Please read the full Membership Agreement below. Signing unlocks once you have scrolled to the end. Your acceptance, signature, timestamp and device details are recorded as an evidential record, and you can download a PDF copy straight after signing.</p>
      {doc ? <AgreementDoc doc={doc} onRead={() => setRead(true)} /> : <p className="text-[color:var(--mute)]">Loading agreement…</p>}
      <p className={`text-sm ${read ? "text-emerald-600" : "text-[color:var(--mute)]"}`} data-testid="gtax-agreement-read-state">{read ? "You have read the agreement to the end." : "Scroll to the end of the agreement to continue."}</p>
      <fieldset disabled={!read} className="space-y-5 disabled:opacity-40">
        {CLAUSES.map((c) => (
          <label key={c.id} className="flex gap-4 items-start cursor-pointer" data-testid={`gtax-clause-${c.id}`}>
            <input type="checkbox" className="gx-check" checked={accepted.includes(c.id)} onChange={() => toggle(c.id)} data-testid={`gtax-clause-${c.id}-checkbox`} />
            <span>{c.label} <Link to={c.link} target="_blank" className="underline text-[color:var(--gold2)]">Read</Link></span>
          </label>
        ))}
        <div className="pt-4">
          <Label>Type your full legal name to sign</Label>
          <input value={sig} onChange={(e) => setSig(e.target.value)} placeholder={f.full_name} className="w-full py-3 gx-serif italic text-3xl" data-testid="gtax-access-signature" />
          {sig && !sigOk && <p className="mt-2 text-sm text-[#A0523F]" data-testid="gtax-access-sig-error">Signature must match: {f.full_name}</p>}
        </div>
      </fieldset>
      <div className="flex flex-wrap gap-4">
        <button onClick={back} className="gx-ghost" data-testid="gtax-access-back">Back</button>
        <button disabled={!read || !all || !sigOk || busy} onClick={submit} className="gx-btn disabled:opacity-40" data-testid="gtax-access-sign">{busy ? "Recording…" : "Accept & sign"}</button>
      </div>
    </div>
  );
};

const TgDone = ({ record }) => (
  <div className="space-y-6" data-testid="gtax-tg-done">
    <div className="gx-eyebrow">Agreement signed · your reference</div>
    <div className="gx-serif text-4xl" data-testid="gtax-tg-done-reference">{record.reference}</div>
    <p className="text-[color:var(--mute)]">You're in. Your settlement details and the bot's commands have been sent to you privately in the GTA X Telegram chat. Choose your network there and paste your transaction ID once sent.</p>
    <div className="flex flex-wrap gap-4">
      <button onClick={() => window.Telegram?.WebApp?.close ? window.Telegram.WebApp.close() : window.history.back()} className="gx-btn" data-testid="gtax-tg-done-close">Return to Telegram</button>
      <AgreementCopy record={record} />
    </div>
  </div>
);

const InviteOnly = () => (
  <div className="space-y-6" data-testid="gtax-access-invite-only">
    <div className="gx-eyebrow">By invitation only</div>
    <p className="gx-serif text-2xl">Membership is by invitation from an existing member.</p>
    <p className="text-[color:var(--mute)]">Please open the personal invitation link a member shared with you. If you believe this is an error, contact us through the Contact page.</p>
    <Link to="/contact" className="gx-ghost inline-block" data-testid="gtax-access-invite-contact">Contact us</Link>
  </div>
);

export default function GtaxAccess() {
  const [params] = useSearchParams();
  const ref = (params.get("ref") || "").trim().toUpperCase();
  const [invite, setInvite] = useState(null);
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ full_name: "", email: "", phone: "", country: "", tier: ALL_TIERS.some((t) => t.id === params.get("tier")) ? params.get("tier") : "standard", amount: "", wallet: "", wallet_network: "" });
  const [accepted, setAccepted] = useState([]);
  const [sig, setSig] = useState("");
  const [read, setRead] = useState(false);
  const [busy, setBusy] = useState(false);
  const [record, setRecord] = useState(null);
  const [tg] = useState(tgInitData);
  useEffect(() => { window.scrollTo(0, 0); }, [step]);
  useEffect(() => {
    if (tg) return setInvite(true);
    if (!ref) return setInvite(false);
    axios.get(`${API}/invite`, { params: { ref } }).then(({ data }) => setInvite(data.valid)).catch(() => setInvite(false));
  }, [tg, ref]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (id) => setAccepted(accepted.includes(id) ? accepted.filter((x) => x !== id) : [...accepted, id]);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/agreements`, { ...f, amount: Number(f.amount), accepted, signature: sig, read_full: read, tg_init_data: tg, ref: tg ? undefined : ref });
      const rec = { ...data, email: f.email.trim().toLowerCase() };
      sessionStorage.setItem("gtax_member", JSON.stringify({ reference: rec.reference, email: rec.email }));
      setRecord(rec);
      setStep(2);
      toast.success("Agreement signed and recorded.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.[0]?.msg || "Could not record your agreement.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="gtax-access-page">
      <PageHead eyebrow="Request access" title="Membership, in four steps." sub={tg ? "Provide your details and sign the digital Membership Agreement. Settlement details are then sent to you privately in Telegram." : "Provide your details, sign the digital Membership Agreement and verify your identity. Settlement details are released once your identity is approved."} />
      <section className="pb-28">
        <div className="gx-wrap max-w-4xl">
          <Steps step={step} tg={!!tg} />
          <div className="gx-card p-8 md:p-12">
            {invite === null && <p className="text-[color:var(--mute)]" data-testid="gtax-access-invite-checking">Checking your invitation…</p>}
            {invite === false && step < 2 && <InviteOnly />}
            {invite && step === 0 && <Details f={f} set={set} next={() => setStep(1)} />}
            {invite && step === 1 && <Agreement f={f} accepted={accepted} toggle={toggle} sig={sig} setSig={setSig} read={read} setRead={setRead} busy={busy} submit={submit} back={() => setStep(0)} />}
            {step === 2 && record && (tg ? <TgDone record={record} /> : <KycPanel record={record} />)}
          </div>
        </div>
      </section>
    </div>
  );
}
